import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { SITE } from "../lib/site";
import iconAsset from "../assets/favicon.png.asset.json";

const GTAG_ID = "G-2YX2R2189T";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A matéria que você procura não existe ou foi movida do arquivo.
        </p>
        <div className="mt-6">
          <Link to="/" className="btn-accent">Voltar para a capa</Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página não carregou
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo deu errado. Você pode tentar novamente ou voltar para a capa.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="btn-primary"
          >
            Tentar novamente
          </button>
          <a href="/" className="btn-ghost">Ir para a capa</a>
        </div>
      </div>
    </div>
  );
}

const ROOT_TITLE = "Gazeta Regional Online — Notícias, Reviews e Guias";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: ROOT_TITLE },
      { name: "description", content: SITE.description },
      { name: "author", content: SITE.name },
      { name: "theme-color", content: "#FBBF24" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: ROOT_TITLE },
      { name: "twitter:title", content: ROOT_TITLE },
      { property: "og:description", content: SITE.description },
      { name: "twitter:description", content: SITE.description },
      { title: "Gazeta Regional Online — Notícias, Reviews e Guias" },
      { property: "og:title", content: "Gazeta Regional Online — Notícias, Reviews e Guias" },
      { name: "twitter:title", content: "Gazeta Regional Online — Notícias, Reviews e Guias" },
      { name: "description", content: "Encontre informacoes e atualizacoes para o dia a dia, acesse nosso jormal online para se manter atualizado" },
      { property: "og:description", content: "Encontre informacoes e atualizacoes para o dia a dia, acesse nosso jormal online para se manter atualizado" },
      { name: "twitter:description", content: "Encontre informacoes e atualizacoes para o dia a dia, acesse nosso jormal online para se manter atualizado" },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/f8d36561-070c-46f2-ac1b-cb23f421b83f/id-preview-baf65f50--7b6fd0f1-7a9a-418e-926e-58cbf100d75a.lovable.app-1783626233051.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/f8d36561-070c-46f2-ac1b-cb23f421b83f/id-preview-baf65f50--7b6fd0f1-7a9a-418e-926e-58cbf100d75a.lovable.app-1783626233051.png" },
    ],
    links: [
      { rel: "icon", href: "/__l5e/assets-v1/3b6451ca-b86e-44d4-a627-13bc34f912bf/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: iconAsset.url },
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        async: true,
        src: `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`,
      },
      {
        children: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GTAG_ID}');
        `,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "NewsMediaOrganization",
              "@id": "/#organization",
              name: SITE.name,
              logo: iconAsset.url,
              description: SITE.description,
              email: SITE.email,
            },
            {
              "@type": "WebSite",
              "@id": "/#website",
              name: SITE.name,
              description: SITE.description,
              inLanguage: "pt-BR",
              publisher: { "@id": "/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1"><Outlet /></main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </QueryClientProvider>
  );
}
